import { DateTime, dateTime } from 'aos-helpers/src/helpers/Time'
import { severityInkColorsMapping } from 'aos-services/src/services/common/types/AosSeverity'
import { TaskCommonProps } from 'aos-services/src/services/tasks/types/task/BaseTask'
import {
    statusTextColorsMapping,
    TaskStatus,
} from 'aos-services/src/services/tasks/types/TaskStatus'
import { Box } from 'aos-ui/src/components/base/Box'
import { InfoBarContent, InfoBarText } from 'aos-ui/src/components/infobar/InfoBar'
import { IconVariant } from 'aos-ui/src/components/svg/Icon'
import { UserAssignment } from 'aos-ui/src/components/ui/UserAssignment'
import { Color } from 'aos-ui-common/src/styles/Color'
import { TFunction } from 'i18next'
import moment from 'moment'
import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { taskIconVariant, taskItemIconVariant } from '../utils'
import { TaskIcon } from './icons'

export interface TaskBarContentProps {
    item: TaskCommonProps
    inTooltip?: boolean
    shorten?: boolean
}

export const TaskBarContent: FC<TaskBarContentProps> = ({
    item,
    inTooltip = false,
    shorten = false,
}) => {
    const { t: translate } = useTranslation()
    const isMuted =
        !item.overdue && (item.status === TaskStatus.Done || item.status === TaskStatus.Cancelled)
    const iconVariant = inTooltip
        ? IconVariant.White
        : isMuted
          ? taskIconVariant[item.status]
          : taskItemIconVariant(false, item)
    const textColor = inTooltip
        ? Color.White
        : isMuted
          ? statusTextColorsMapping[item.status]
          : severityInkColorsMapping[item.severity]

    const titleFragment = (
        <>
            <TaskIcon iconVariant={iconVariant} category={item.category} />
            <InfoBarText
                size={14}
                weight={inTooltip ? 'semiBold' : 'regular'}
                color={textColor}
                overflow='hidden'
                lineHeight='large'
                paddingLeft={8}
                data-timeline-text
            >
                {item.title}
            </InfoBarText>
        </>
    )

    const userFragment = !shorten && (
        <Box marginLeft={8}>
            <UserAssignment
                iconVariant={iconVariant}
                textColor={textColor}
                users={item.assignees}
                onWhite={false}
                textSize={inTooltip ? 14 : 12}
                weight={inTooltip ? 'semiBold' : undefined}
            />
        </Box>
    )

    return inTooltip ? (
        <InfoBarContent
            inTooltip={inTooltip}
            wrap
            paddingLeft={8}
            paddingRight={8}
            paddingVertical={2}
            overflow='hidden'
        >
            <Box row>
                <Box flex={1} row>
                    {titleFragment}
                </Box>
                <Box>{userFragment}</Box>
            </Box>
            <Box row>
                {item.lastComment && (
                    <InfoBarText
                        flex={1}
                        size={14}
                        color={textColor}
                        overflow='hidden'
                        lineHeight='large'
                        paddingLeft={8}
                        data-timeline-text
                    >
                        {`"${item.lastComment}"`}
                    </InfoBarText>
                )}
                {item.overdue && (
                    <InfoBarText
                        size={13}
                        color={textColor}
                        overflow='hidden'
                        lineHeight='large'
                        paddingLeft={8}
                        data-timeline-text
                    >
                        {formatOverdue(translate, item.realEndTime)}
                    </InfoBarText>
                )}
            </Box>
        </InfoBarContent>
    ) : (
        <InfoBarContent
            inTooltip={inTooltip}
            row
            paddingLeft={8}
            paddingRight={8}
            paddingVertical={2}
            overflow='hidden'
        >
            {titleFragment}

            {item.lastComment && (
                <InfoBarText
                    size={14}
                    color={textColor}
                    overflow='hidden'
                    lineHeight='large'
                    flex={1}
                    textAlign='right'
                    paddingLeft={8}
                    paddingRight={8}
                    data-timeline-text
                >
                    {`"${item.lastComment}"`}
                </InfoBarText>
            )}
            {userFragment}
        </InfoBarContent>
    )
}

const formatOverdue = (translate: TFunction, endTime?: DateTime) => {
    return `${translate('task-filter.overdue', { ns: 'tasks' })}: ${moment
        .duration(dateTime().diff(endTime))
        .format('h [hrs], m [min]')}`
}
